const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Seeded deterministic "random" generator (no Math.random for reproducibility) ──
function seededRand(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

// ── Diagram 1: Random Process — Time History and Statistics ───────
function DiagramRandomProcess() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;
  const rand = seededRand(42);

  // Generate a broadband random signal (sum of sinusoids with random phases)
  const N = 300;
  const t = Array.from({ length: N }, (_, i) => i / (N - 1) * 10);
  const sig = t.map(tv => {
    let v = 0;
    for (let k = 1; k <= 20; k++) {
      v += (1 / k) * Math.cos(k * 1.2 * tv + rand() * 2 * Math.PI);
    }
    return v / 4;
  });

  // Statistics
  const mean = sig.reduce((a, b) => a + b, 0) / N;
  const variance = sig.reduce((a, b) => a + (b - mean) ** 2, 0) / N;
  const sigma = Math.sqrt(variance);

  // Gaussian PDF
  const xPDF = Array.from({ length: 100 }, (_, i) => -3.5 * sigma + i / 99 * 7 * sigma);
  const pdf = xPDF.map(x => (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * ((x - mean) / sigma) ** 2));

  const ox = 14, oy = 180, ow = PW - 28, oh = 130;
  const toXt = tv => ox + (tv / 10) * ow;
  const toYt = v => oy - Math.max(-1, Math.min(1, v)) * oh * 0.85;

  const ox2 = PW + 36, oy2 = 260, ow2 = 80, oh2 = 190;
  const toXp = x => ox2 + (x + 3.5 * sigma) / (7 * sigma) * ow2;
  const toYp = p => oy2 - (p / Math.max(...pdf)) * oh2 * 0.88;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Random Process — Time History, Ensemble, and Probability Density</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="rp1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Time histories (3 sample records = ensemble) */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Ensemble of Sample Records — x(t, θ₁), x(t, θ₂), x(t, θ₃)</text>

        {/* Three sample functions with different seeds */}
        {[
          { seed: 42, col: '#c0392b', yOff: -90, label: 'x(t, θ₁)' },
          { seed: 137, col: '#2E75B6', yOff: 0,   label: 'x(t, θ₂)' },
          { seed: 251, col: '#27ae60', yOff: 90,   label: 'x(t, θ₃)' },
        ].map(({ seed, col, yOff, label }) => {
          const r2 = seededRand(seed);
          const s2 = t.map(tv => {
            let v = 0;
            for (let k = 1; k <= 20; k++) {
              v += (1 / k) * Math.cos(k * 1.2 * tv + r2() * 2 * Math.PI);
            }
            return v / 4;
          });
          const baseY = oy + yOff;
          const pts = s2.map((v, i) => `${toXt(t[i])},${baseY - Math.max(-1, Math.min(1, v)) * 32}`).join(' ');
          return (
            <g key={seed}>
              <line x1={ox} y1={baseY} x2={ox + ow} y2={baseY} stroke="#eee" strokeWidth="1" />
              <polyline points={pts} fill="none" stroke={col} strokeWidth="1.5" opacity="0.9" />
              <text x={ox - 4} y={baseY + 4} textAnchor="end" fontSize="10" fill={col} fontWeight="700">{label}</text>
            </g>
          );
        })}

        {/* Ensemble average (fixed time, vary over samples) */}
        <line x1={toXt(5)} y1={oy - 120} x2={toXt(5)} y2={oy + 108} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,2" />
        <text x={toXt(5) + 4} y={oy - 116} fontSize="9.5" fill="#e07b00" fontWeight="700">t = t₁</text>
        <text x={toXt(5) + 4} y={oy - 104} fontSize="9" fill="#e07b00">ensemble avg</text>

        {/* t-axis */}
        <line x1={ox} y1={oy + 110} x2={ox + ow + 10} y2={oy + 110} stroke="#888" strokeWidth="1.5" markerEnd="url(#rp1)" />
        <text x={ox + ow + 14} y={oy + 114} fontSize="10" fill="#555">t (s)</text>
        {[0, 2, 4, 6, 8, 10].map(tv => (
          <text key={tv} x={toXt(tv)} y={oy + 122} textAnchor="middle" fontSize="9" fill="#aaa">{tv}</text>
        ))}

        {/* Panel 2: PDF + statistics */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Probability Density Function (PDF) — Stationary Process</text>

        {/* Gaussian PDF */}
        <polyline
          points={xPDF.map((x, i) => `${toXp(x)},${toYp(pdf[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />
        {/* Fill under PDF */}
        <polygon
          points={`${toXp(xPDF[0])},${oy2} ` + xPDF.map((x, i) => `${toXp(x)},${toYp(pdf[i])}`).join(' ') + ` ${toXp(xPDF[xPDF.length - 1])},${oy2}`}
          fill="#c0392b" opacity="0.08"
        />

        {/* ±σ, ±2σ lines */}
        {[
          { s: sigma, col: '#c0392b', pct: '68%', label: '±σ' },
          { s: 2 * sigma, col: '#e07b00', pct: '95%', label: '±2σ' },
          { s: 3 * sigma, col: '#2E75B6', pct: '99.7%', label: '±3σ' },
        ].map(({ s, col, pct, label }) => {
          const xL = toXp(mean - s), xR = toXp(mean + s);
          const yH = toYp(pdf[Math.round((s + 3.5 * sigma) / (7 * sigma) * 99)]);
          return (
            <g key={label}>
              <line x1={xL} y1={oy2} x2={xL} y2={yH} stroke={col} strokeWidth="1.2" strokeDasharray="4,2" opacity="0.7" />
              <line x1={xR} y1={oy2} x2={xR} y2={yH} stroke={col} strokeWidth="1.2" strokeDasharray="4,2" opacity="0.7" />
              <line x1={xL} y1={yH - 4} x2={xR} y2={yH - 4} stroke={col} strokeWidth="1.2" markerEnd="url(#rp1)" />
              <text x={(xL + xR) / 2} y={yH - 8} textAnchor="middle" fontSize="8.5" fill={col} fontWeight="700">{label} ({pct})</text>
            </g>
          );
        })}

        {/* Mean line */}
        <line x1={toXp(mean)} y1={oy2} x2={toXp(mean)} y2={toYp(Math.max(...pdf)) - 6} stroke="#7B0D0D" strokeWidth="1.5" strokeDasharray="3,2" />
        <text x={toXp(mean) + 4} y={toYp(Math.max(...pdf)) - 8} fontSize="9.5" fill="#7B0D0D" fontWeight="700">μ = {mean.toFixed(2)}</text>

        {/* Axis */}
        <line x1={ox2 - 4} y1={oy2} x2={ox2 + ow2 + 10} y2={oy2} stroke="#888" strokeWidth="1.5" markerEnd="url(#rp1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={oy2 - oh2 - 10} stroke="#888" strokeWidth="1.5" markerEnd="url(#rp1)" />
        <text x={ox2 + ow2 + 14} y={oy2 + 4} fontSize="10" fill="#555">x</text>
        <text x={ox2 + 4} y={oy2 - oh2 - 12} fontSize="10" fill="#555">p(x)</text>

        {/* Statistics summary box */}
        <rect x={PW + 130} y={50} width={PW - 120} height={150} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={PW + 136} y={66} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Gaussian (Normal) PDF:</text>
        <text x={PW + 136} y={82} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">p(x) = (1/σ√2π)·e^(-x²/2σ²)</text>
        <text x={PW + 136} y={100} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Key statistics:</text>
        {[
          'Mean: μ = E[x(t)]',
          'Variance: σ² = E[(x−μ)²]',
          'RMS: x_rms = √(μ² + σ²)',
          'Skewness: γ₁ = E[(x−μ)³]/σ³',
          'Kurtosis: γ₂ = E[(x−μ)⁴]/σ⁴',
          'Gaussian: γ₁=0, γ₂=3',
        ].map((line, li) => (
          <text key={li} x={PW + 136} y={116 + li * 14} fontSize="9.5" fill="#333">{line}</text>
        ))}
      </svg>
      <div style={S.dCaption}>A random process is a family of time histories (sample functions), each a realisation of the underlying probability model. At any fixed time t₁, the values across the ensemble form a probability distribution. For a stationary ergodic process, the time average of one record equals the ensemble average across all records — this equivalence is fundamental to practical measurement, since we usually have only one time history. A zero-mean Gaussian process is fully characterised by its variance (or equivalently, its RMS value and power spectral density).</div>
    </div>
  );
}

// ── Diagram 2: Autocorrelation and Power Spectral Density ─────────
function DiagramPSD() {
  const W = 900, H = 370;
  const PW = (W - 30) / 2;

  // Autocorrelation: R(τ) = σ² e^(-α|τ|) cos(ω₀τ) for bandlimited noise
  const tauVals = Array.from({ length: 200 }, (_, i) => -6 + i / 199 * 12);
  const alpha = 0.6, omega0 = 2.5, sigSq = 1.0;
  const Rtau = tauVals.map(tau => sigSq * Math.exp(-alpha * Math.abs(tau)) * Math.cos(omega0 * tau));

  // Corresponding PSD (Lorentzian peaks)
  const nFreq = 300;
  const fVals = Array.from({ length: nFreq }, (_, i) => i / (nFreq - 1) * 5);
  const Sf = fVals.map(f => {
    const w = 2 * Math.PI * f;
    // PSD as Fourier transform of R(τ): two Lorentzian peaks
    return sigSq * alpha / Math.PI * (1 / (alpha ** 2 + (w - omega0) ** 2) + 1 / (alpha ** 2 + (w + omega0) ** 2));
  });

  // White noise PSD (flat)
  const whiteS = fVals.map(() => 0.12);

  // Band-limited white noise
  const bandS = fVals.map(f => (f >= 0.5 && f <= 2.5) ? 0.28 : 0.0);

  const ox1 = 14, oy1 = 170, ow1 = PW - 28, oh1 = 130;
  const toXtau = tau => ox1 + (tau + 6) / 12 * ow1;
  const toYR = v => oy1 - v / 1.1 * oh1 * 0.85;

  const ox2 = PW + 36, oy2 = 270, ow2 = PW - 50, oh2 = 210;
  const toXf = f => ox2 + (f / 5) * ow2;
  const toYS = v => oy2 - Math.min(v / 0.35, 1) * oh2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Autocorrelation Function R(τ) and Power Spectral Density S(f)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="psd1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Autocorrelation */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Autocorrelation Function R(τ)</text>
        <text x={10 + PW / 2} y={42} textAnchor="middle" fontSize="10" fill="#555">R(τ) = E[x(t)·x(t+τ)]  — measures temporal memory</text>

        {/* Grid */}
        {[-1, 0, 0.5, 1].map(v => {
          const y = toYR(v);
          return (
            <g key={v}>
              <line x1={ox1} y1={y} x2={ox1 + ow1} y2={y} stroke={v === 0 ? '#e8e8e8' : '#f5f5f5'} strokeWidth="1" />
              <text x={ox1 - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
            </g>
          );
        })}

        {/* R(τ) curves for different processes */}
        {/* Bandlimited noise */}
        <polyline
          points={tauVals.map((tau, i) => `${toXtau(tau)},${toYR(Rtau[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />

        {/* White noise: delta function at τ=0 */}
        {(() => {
          const x0 = toXtau(0);
          return (
            <g>
              <line x1={x0} y1={toYR(0)} x2={x0} y2={toYR(1.0)} stroke="#2E75B6" strokeWidth="3" />
              <polygon points={`${x0 - 4},${toYR(1.0) + 8} ${x0},${toYR(1.0) - 2} ${x0 + 4},${toYR(1.0) + 8}`} fill="#2E75B6" />
              <text x={x0 + 6} y={toYR(0.9)} fontSize="9" fill="#2E75B6" fontWeight="700">White noise: R(τ) = S₀δ(τ)</text>
            </g>
          );
        })()}

        {/* Key values */}
        <circle cx={toXtau(0)} cy={toYR(sigSq)} r={4} fill="#c0392b" />
        <text x={toXtau(0) + 6} y={toYR(sigSq) - 4} fontSize="9.5" fill="#c0392b" fontWeight="700">R(0) = σ² (variance)</text>

        {/* Correlation length annotation */}
        {(() => {
          const tau_c = 1 / alpha;
          const x1 = toXtau(0), x2 = toXtau(tau_c);
          const yLine = toYR(-0.18);
          return (
            <g>
              <line x1={x1} y1={yLine} x2={x2} y2={yLine} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#psd1)" />
              <line x1={x1} y1={yLine - 4} x2={x1} y2={yLine + 4} stroke="#27ae60" strokeWidth="1.5" />
              <text x={(x1 + x2) / 2} y={yLine - 6} textAnchor="middle" fontSize="9" fill="#27ae60" fontWeight="700">τ_c = 1/α (correlation time)</text>
            </g>
          );
        })()}

        {/* Axes */}
        <line x1={ox1} y1={oy1} x2={ox1 + ow1 + 10} y2={oy1} stroke="#888" strokeWidth="1.5" markerEnd="url(#psd1)" />
        <line x1={toXtau(0)} y1={oy1 + oh1 * 0.2} x2={toXtau(0)} y2={toYR(1.1) - 10} stroke="#888" strokeWidth="1.5" markerEnd="url(#psd1)" />
        <text x={ox1 + ow1 + 14} y={oy1 + 4} fontSize="10" fill="#555">τ</text>
        <text x={toXtau(0) + 4} y={toYR(1.1) - 12} fontSize="10" fill="#555">R</text>
        {[-4, -2, 0, 2, 4].map(tv => (
          <text key={tv} x={toXtau(tv)} y={oy1 + 12} textAnchor="middle" fontSize="9" fill="#aaa">{tv}</text>
        ))}

        {/* Wiener-Khinchin box */}
        <rect x={14} y={H - 78} width={PW - 20} height={62} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={20} y={H - 64} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Wiener-Khinchin Theorem:</text>
        <text x={20} y={H - 50} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">S(f) = ∫ R(τ)·e^(-j2πfτ) dτ</text>
        <text x={20} y={H - 36} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">R(τ) = ∫ S(f)·e^(+j2πfτ) df</text>
        <text x={20} y={H - 20} fontSize="9.5" fill="#555">R(0) = σ² = ∫ S(f) df  (Parseval — area under PSD = variance)</text>

        {/* Panel 2: PSD plots */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Power Spectral Density S(f) — Three Process Types</text>

        {/* Grid */}
        {[0, 0.1, 0.2, 0.3].map(v => {
          const y = toYS(v);
          return (
            <g key={v}>
              <line x1={ox2} y1={y} x2={ox2 + ow2} y2={y} stroke="#f5f5f5" strokeWidth="1" />
              <text x={ox2 - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
            </g>
          );
        })}

        {/* White noise (flat) */}
        <polyline
          points={fVals.map((f, i) => `${toXf(f)},${toYS(whiteS[i])}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2" strokeDasharray="6,3"
        />
        <text x={toXf(4)} y={toYS(0.12) - 6} textAnchor="end" fontSize="9.5" fill="#2E75B6" fontWeight="700">White noise: S = S₀ (flat)</text>

        {/* Band-limited white noise */}
        {(() => {
          const pts = fVals.map((f, i) => `${toXf(f)},${toYS(bandS[i])}`).join(' ');
          return (
            <g>
              <polyline points={pts} fill="none" stroke="#27ae60" strokeWidth="2.5" />
              <polygon
                points={`${toXf(0.5)},${oy2} ${toXf(0.5)},${toYS(0.28)} ${toXf(2.5)},${toYS(0.28)} ${toXf(2.5)},${oy2}`}
                fill="#27ae60" opacity="0.1"
              />
              <text x={toXf(1.5)} y={toYS(0.28) - 6} textAnchor="middle" fontSize="9.5" fill="#27ae60" fontWeight="700">Band-limited white</text>
            </g>
          );
        })()}

        {/* Bandlimited noise PSD (two peaks) */}
        <polyline
          points={fVals.map((f, i) => `${toXf(f)},${toYS(Sf[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />
        {(() => {
          const f0 = omega0 / (2 * Math.PI);
          const x0 = toXf(f0);
          const y0 = toYS(Sf[Math.round(f0 / 5 * (nFreq - 1))]);
          return (
            <g>
              <circle cx={x0} cy={y0} r={4} fill="#c0392b" />
              <text x={x0 + 6} y={y0 - 4} fontSize="9.5" fill="#c0392b" fontWeight="700">Bandlimited noise</text>
              <text x={x0 + 6} y={y0 + 8} fontSize="9" fill="#c0392b">(peak at f₀ = ω₀/2π)</text>
            </g>
          );
        })()}

        {/* Area = variance annotation */}
        <text x={toXf(3)} y={toYS(0.05)} fontSize="9.5" fill="#c0392b" fontStyle="italic">Area = σ² = ∫S(f)df</text>

        {/* Axes */}
        <line x1={ox2} y1={oy2 + 4} x2={ox2 + ow2 + 10} y2={oy2 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#psd1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={toYS(0.35) - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#psd1)" />
        <text x={ox2 + ow2 + 14} y={oy2 + 8} fontSize="10" fill="#555">f (Hz)</text>
        <text x={ox2 + 4} y={toYS(0.35) - 10} fontSize="10" fill="#555">S(f)</text>
        {[0, 1, 2, 3, 4, 5].map(fv => (
          <text key={fv} x={toXf(fv)} y={oy2 + 16} textAnchor="middle" fontSize="9" fill="#aaa">{fv}</text>
        ))}

        {/* Legend */}
        {[
          { col: '#2E75B6', dash: '6,3', label: 'White noise (S₀ = const)' },
          { col: '#27ae60', dash: 'none', label: 'Band-limited white' },
          { col: '#c0392b', dash: 'none', label: 'Bandlimited random (correlated)' },
        ].map(({ col, dash, label }, i) => (
          <g key={i}>
            <line x1={ox2 + 8} y1={H - 52 + i * 16} x2={ox2 + 28} y2={H - 52 + i * 16}
              stroke={col} strokeWidth="2" strokeDasharray={dash} />
            <text x={ox2 + 32} y={H - 48 + i * 16} fontSize="9.5" fill={col} fontWeight="700">{label}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The autocorrelation function R(τ) = E[x(t)·x(t+τ)] measures how strongly a signal at time t is correlated with itself τ seconds later. R(0) = σ² (the variance). The Wiener-Khinchin theorem states that the PSD S(f) is the Fourier transform of R(τ) — they are a transform pair. Integrating the PSD over all frequencies recovers the variance: σ² = ∫S(f)df. White noise has a flat PSD (all frequencies equally present); bandlimited processes have non-zero PSD only in a finite band; coloured noise has frequency-shaped PSD.</div>
    </div>
  );
}

// ── Diagram 3: SDOF Response to Random Excitation ─────────────────
function DiagramSDOFRandom() {
  const W = 900, H = 360;
  const PW = (W - 30) / 2;

  // Input PSD: white noise S_FF = S0
  const S0 = 1.0;
  // SDOF parameters
  const wn = 6.28, zeta = 0.05; // fn = 1 Hz
  const m = 1.0;

  // Transfer function H(f) = 1/(m(-ω²+2jζωₙω+ωₙ²))
  // Response PSD: S_XX(f) = |H(f)|² * S_FF(f)
  const nFreq = 400;
  const fVals = Array.from({ length: nFreq }, (_, i) => 0.01 + i / (nFreq - 1) * 4);
  const HmagSq = fVals.map(f => {
    const w = 2 * Math.PI * f;
    const r = w / wn;
    return (1 / (m * wn * wn)) ** 2 / ((1 - r * r) ** 2 + (2 * zeta * r) ** 2);
  });
  const SXX = HmagSq.map(h => h * S0);

  // Variance from integration
  const df = fVals[1] - fVals[0];
  const sigmaX_sq = SXX.reduce((a, b) => a + b, 0) * df;
  const sigmaX = Math.sqrt(sigmaX_sq);

  const ox = 14, oy = 60, ow = PW - 28, plotH = 200;
  const fMax = 4, SMax = 0.015;
  const toXf = f => ox + (f / fMax) * ow;
  const toYS = v => oy + plotH - Math.min(v / SMax, 1) * plotH;

  // Transfer function magnitude (dB) for right panel
  const nF2 = 300;
  const f2Vals = Array.from({ length: nF2 }, (_, i) => 0.05 + i / (nF2 - 1) * 5);
  const HdB = f2Vals.map(f => {
    const w = 2 * Math.PI * f;
    const r = w / wn;
    const hmag = (1 / (m * wn * wn)) / Math.sqrt((1 - r * r) ** 2 + (2 * zeta * r) ** 2);
    return 20 * Math.log10(hmag);
  });

  const ox2 = PW + 36, oy2 = 60, ow2 = PW - 50, plotH2 = 250;
  const toXf2 = f => ox2 + Math.log10(f / 0.05) / Math.log10(5 / 0.05) * ow2;
  const toYH = db => oy2 + plotH2 - Math.max(0, Math.min((db + 50) / 50, 1)) * plotH2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>SDOF System Response to White Noise Excitation — Input/Output PSD Relationship</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="sr1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
          <marker id="sr2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Panel 1: Input and output PSD */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Input PSD (white) and Output PSD — S_XX = |H|² S_FF</text>

        {/* Grid */}
        {[0, 0.005, 0.010, 0.015].map(v => {
          const y = toYS(v);
          return (
            <g key={v}>
              <line x1={ox} y1={y} x2={ox + ow} y2={y} stroke="#f5f5f5" strokeWidth="1" />
              <text x={ox - 4} y={y + 4} textAnchor="end" fontSize="8.5" fill="#aaa">{v}</text>
            </g>
          );
        })}
        {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map(f => {
          const x = toXf(f);
          return (
            <g key={f}>
              <line x1={x} y1={oy} x2={x} y2={oy + plotH} stroke="#f5f5f5" strokeWidth="1" />
              <text x={x} y={oy + plotH + 12} textAnchor="middle" fontSize="9" fill="#aaa">{f}</text>
            </g>
          );
        })}

        {/* Input PSD (flat white noise) */}
        <line x1={ox} y1={toYS(S0 * HmagSq[0])} x2={ox + ow} y2={toYS(S0 * HmagSq[0])}
          stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="6,3" opacity="0.5" />
        <text x={ox + 10} y={toYS(S0 * HmagSq[0]) - 4} fontSize="9.5" fill="#2E75B6">S_FF = S₀ (white noise input)</text>

        {/* Output PSD */}
        <polyline
          points={fVals.map((f, i) => `${toXf(f)},${toYS(SXX[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />
        {/* Fill under output PSD */}
        <polygon
          points={`${toXf(fVals[0])},${oy + plotH} ` +
            fVals.map((f, i) => `${toXf(f)},${toYS(SXX[i])}`).join(' ') +
            ` ${toXf(fVals[fVals.length - 1])},${oy + plotH}`}
          fill="#c0392b" opacity="0.07"
        />

        {/* Peak annotation */}
        {(() => {
          const fPeak = wn / (2 * Math.PI);
          const xPk = toXf(fPeak);
          const yPk = toYS(SXX[Math.round(fPeak / fMax * (nFreq - 1))]);
          return (
            <g>
              <line x1={xPk} y1={yPk + 4} x2={xPk} y2={yPk + 20} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#sr2)" />
              <text x={xPk + 4} y={yPk + 36} fontSize="9.5" fill="#c0392b" fontWeight="700">fₙ = 1 Hz (ζ=0.05)</text>
              <text x={xPk + 4} y={yPk + 48} fontSize="9" fill="#c0392b">Peak = S₀/(4ζ²ωₙ⁴m²)</text>
            </g>
          );
        })()}

        {/* σ² annotation */}
        <text x={ox + ow - 4} y={oy + 20} textAnchor="end" fontSize="9.5" fill="#c0392b">σ_X² = S₀π/(4ζωₙ³m²)</text>
        <text x={ox + ow - 4} y={oy + 34} textAnchor="end" fontSize="9.5" fill="#c0392b">= {sigmaX_sq.toFixed(4)} m²</text>

        {/* Axes */}
        <line x1={ox} y1={oy} x2={ox} y2={oy + plotH + 4} stroke="#888" strokeWidth="1.5" />
        <line x1={ox} y1={oy + plotH} x2={ox + ow + 8} y2={oy + plotH} stroke="#888" strokeWidth="1.5" markerEnd="url(#sr1)" />
        <text x={ox + ow / 2} y={oy + plotH + 24} textAnchor="middle" fontSize="10" fill="#555">Frequency f (Hz)</text>
        <text x={ox - 18} y={oy + plotH / 2} fontSize="9.5" fill="#555" transform={`rotate(-90,${ox - 18},${oy + plotH / 2})`} textAnchor="middle">S(f) (m²/Hz)</text>

        {/* Formula box */}
        <rect x={14} y={H - 66} width={PW - 20} height={50} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={20} y={H - 52} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Input-Output PSD relation:</text>
        <text x={20} y={H - 38} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">S_XX(f) = |H(f)|² · S_FF(f)</text>
        <text x={20} y={H - 24} fontSize="9.5" fill="#333">σ_X² = ∫|H(f)|² S_FF(f) df  (mean square response)</text>

        {/* Panel 2: Frequency response function (log scale) */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">|H(f)| Frequency Response — SDOF Transfer Function</text>

        {/* Grid dB */}
        {[-50, -40, -30, -20, -10, 0].map(db => {
          const y = toYH(db);
          if (y < oy2 || y > oy2 + plotH2) return null;
          return (
            <g key={db}>
              <line x1={ox2} y1={y} x2={ox2 + ow2} y2={y} stroke={db === 0 ? '#e0e0e0' : '#f5f5f5'} strokeWidth="1" />
              <text x={ox2 - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#aaa">{db}</text>
            </g>
          );
        })}

        {/* Different damping curves */}
        {[
          { z: 0.02, col: '#c0392b', label: 'ζ=0.02' },
          { z: 0.05, col: '#e07b00', label: 'ζ=0.05' },
          { z: 0.15, col: '#2E75B6', label: 'ζ=0.15' },
          { z: 0.5,  col: '#27ae60', label: 'ζ=0.50' },
        ].map(({ z, col, label }) => {
          const pts = f2Vals.map(f => {
            const w = 2 * Math.PI * f;
            const r = w / wn;
            const hmag = 1 / Math.sqrt((1 - r * r) ** 2 + (2 * z * r) ** 2);
            return `${toXf2(f)},${toYH(20 * Math.log10(hmag))}`;
          }).join(' ');
          return <polyline key={z} points={pts} fill="none" stroke={col} strokeWidth="2" />;
        })}

        {/* Axes (log freq) */}
        <line x1={ox2} y1={oy2} x2={ox2} y2={oy2 + plotH2 + 4} stroke="#888" strokeWidth="1.5" />
        <line x1={ox2} y1={oy2 + plotH2} x2={ox2 + ow2 + 8} y2={oy2 + plotH2} stroke="#888" strokeWidth="1.5" markerEnd="url(#sr1)" />
        {[0.1, 0.5, 1, 2, 5].map(f => {
          const x = toXf2(f);
          if (x < ox2 || x > ox2 + ow2) return null;
          return (
            <g key={f}>
              <line x1={x} y1={oy2} x2={x} y2={oy2 + plotH2} stroke="#f0f0f0" strokeWidth="1" />
              <text x={x} y={oy2 + plotH2 + 12} textAnchor="middle" fontSize="9" fill="#aaa">{f}</text>
            </g>
          );
        })}
        <text x={ox2 + ow2 / 2} y={oy2 + plotH2 + 24} textAnchor="middle" fontSize="10" fill="#555">Frequency (Hz) — log scale</text>
        <text x={ox2 - 20} y={oy2 + plotH2 / 2} fontSize="9.5" fill="#555" transform={`rotate(-90,${ox2 - 20},${oy2 + plotH2 / 2})`} textAnchor="middle">|H(f)| (dB)</text>

        {/* Legend */}
        {[
          { col: '#c0392b', label: 'ζ=0.02' },
          { col: '#e07b00', label: 'ζ=0.05' },
          { col: '#2E75B6', label: 'ζ=0.15' },
          { col: '#27ae60', label: 'ζ=0.50' },
        ].map(({ col, label }, i) => (
          <g key={i}>
            <line x1={ox2 + 8} y1={H - 52 + i * 14} x2={ox2 + 26} y2={H - 52 + i * 14} stroke={col} strokeWidth="2" />
            <text x={ox2 + 30} y={H - 48 + i * 14} fontSize="9.5" fill={col}>{label}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The fundamental result of linear random vibration theory is S_XX(f) = |H(f)|²·S_FF(f): the output PSD equals the squared magnitude of the frequency response function multiplied by the input PSD. For white noise input, the output PSD has the shape of |H|², with a sharp resonance peak for lightly damped systems. The mean-square response σ_X² = S₀π/(4ζωₙ³m²) — inversely proportional to both damping and the cube of natural frequency. Lightly damped resonances concentrate enormous energy in a narrow frequency band.</div>
    </div>
  );
}

// ── Diagram 4: Stationarity, Ergodicity, Gaussian Tests ──────────
function DiagramStatProps() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  const rand = seededRand(77);
  const N = 250;
  const t = Array.from({ length: N }, (_, i) => i / (N - 1) * 10);

  // Stationary signal
  const statSig = t.map(tv => {
    let v = 0;
    for (let k = 1; k <= 12; k++) {
      v += (1 / k) * Math.cos(k * 1.5 * tv + rand() * 2 * Math.PI);
    }
    return v / 3;
  });

  // Non-stationary: amplitude grows with time
  const nonStat = statSig.map((v, i) => v * (1 + 0.3 * t[i]));

  const ox = 14, oy = 100, ow = PW - 28, oh = 70;
  const toXt = tv => ox + (tv / 10) * ow;
  const toY = (v, sig) => oy + oh / 2 - Math.max(-2, Math.min(2, v / Math.max(...sig.map(Math.abs))) * oh / 2);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Stationarity, Ergodicity, and Gaussian Process Properties</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="sp1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Stationary vs non-stationary */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Stationary vs. Non-Stationary Process</text>

        {/* Stationary signal */}
        <text x={ox} y={48} fontSize="10.5" fill="#27ae60" fontWeight="700">Stationary: constant statistics over time</text>
        <polyline
          points={statSig.map((v, i) => `${toXt(t[i])},${toY(v, statSig)}`).join(' ')}
          fill="none" stroke="#27ae60" strokeWidth="1.8"
        />
        {/* Running RMS (flat) */}
        {(() => {
          const rms = Math.sqrt(statSig.reduce((a, b) => a + b * b, 0) / N);
          return (
            <g>
              <line x1={ox} y1={toY(rms, statSig)} x2={ox + ow} y2={toY(rms, statSig)} stroke="#27ae60" strokeWidth="1" strokeDasharray="4,2" opacity="0.5" />
              <line x1={ox} y1={toY(-rms, statSig)} x2={ox + ow} y2={toY(-rms, statSig)} stroke="#27ae60" strokeWidth="1" strokeDasharray="4,2" opacity="0.5" />
              <text x={ox + ow - 4} y={toY(rms, statSig) - 3} textAnchor="end" fontSize="8.5" fill="#27ae60">±σ</text>
            </g>
          );
        })()}

        {/* Non-stationary signal */}
        <text x={ox} y={148} fontSize="10.5" fill="#c0392b" fontWeight="700">Non-stationary: variance grows with time</text>
        <polyline
          points={nonStat.map((v, i) => `${toXt(t[i])},${160 - Math.max(-3, Math.min(3, v)) * 22}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="1.8"
        />
        {/* Growing envelope */}
        {(() => {
          const envPts = t.map(tv => `${toXt(tv)},${160 - (1 + 0.3 * tv) * 22}`);
          const envPtsNeg = t.slice().reverse().map(tv => `${toXt(tv)},${160 + (1 + 0.3 * tv) * 22}`);
          return (
            <polygon
              points={envPts.join(' ') + ' ' + envPtsNeg.join(' ')}
              fill="#c0392b" opacity="0.06"
            />
          );
        })()}

        {/* x-axis */}
        <line x1={ox} y1={H - 36} x2={ox + ow + 8} y2={H - 36} stroke="#888" strokeWidth="1.5" markerEnd="url(#sp1)" />
        <text x={ox + ow + 12} y={H - 32} fontSize="10" fill="#555">t</text>

        {/* Ergodicity note */}
        <rect x={14} y={H - 30} width={PW - 20} height={18} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1" />
        <text x={20} y={H - 18} fontSize="9.5" fill="#27ae60" fontWeight="700">Ergodic: time average = ensemble average. Required for single-record analysis.</text>

        {/* Panel 2: Process properties cards */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Key Random Process Properties</text>

        {[
          { y: 36, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Stationarity',
            lines: ['Strict: all joint distributions time-invariant', 'Wide-sense (WSS): constant mean + R(τ) depends only on τ', 'Required for PSD to be well-defined', 'Test: split record into segments, compare statistics'] },
          { y: 114, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Ergodicity',
            lines: ['Time averages equal ensemble averages', 'Allows single record to represent the process', 'Sufficient condition: R(τ) → 0 as τ → ∞', 'Most physical random vibration: ergodic WSS'] },
          { y: 192, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Gaussian (Normal) Process',
            lines: ['All finite-dimensional distributions are Gaussian', 'Fully characterised by mean μ(t) and R(τ)', 'Linear system output is Gaussian if input is Gaussian', 'Central limit theorem: sum of many independent → Gaussian'] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={PW + 26} y={y} width={PW - 14} height={lines.length * 17 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={PW + 32} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={PW + 32} y={y + 30 + li * 17} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Stationarity (wide-sense: constant mean, R depending only on lag τ) is the essential assumption for PSD analysis. Non-stationary signals — transients, startup, ramp loading — require time-frequency methods (spectrogram, wavelet transform). Ergodicity allows a single long time record to estimate all statistical properties. The Gaussian assumption is justified by the central limit theorem when many independent sources contribute, and is preserved through linear systems — making it the default model for structural random vibration analysis.</div>
    </div>
  );
}

// ── Diagram 5: Miles' Equation and RMS Response ───────────────────
function DiagramMiles() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Miles' Equation — RMS Response to Broadband Random Excitation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="me1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
          <marker id="me2" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Central Miles formula display */}
        <rect x={10} y={10} width={560} height={80} rx={6} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2.5" />
        <text x={290} y={36} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Miles' Equation (white noise input PSD = S₀):</text>
        <text x={290} y={58} textAnchor="middle" fontSize="16" fill="#c0392b" fontFamily="'Courier New',monospace" fontWeight="700">x_rms = √(π·fₙ·S₀ / (2·ζ))</text>
        <text x={290} y={76} textAnchor="middle" fontSize="10.5" fill="#555">Acceleration rms = √(π·fₙ·G_in / (2·ζ))  where G_in = input PSD [g²/Hz]</text>

        {/* Derivation steps */}
        {[
          { x: 10, y: 100, w: 130, h: 58, col: '#c0392b', bg: '#fff5f5', bc: '#c0392b',
            title: 'Step 1', lines: ['σ² = ∫|H(f)|² S₀ df', '(integrate over all f)'] },
          { x: 152, y: 100, w: 150, h: 58, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Step 2', lines: ['Assume S₀ flat (white)', 'σ² = S₀ ∫|H(f)|² df'] },
          { x: 316, y: 100, w: 160, h: 58, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Step 3', lines: ['Evaluate integral:', '∫|H|²df = π fₙ/(2ζ k²)'] },
          { x: 490, y: 100, w: 136, h: 58, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
            title: 'Result', lines: ['x_rms = √(πfₙS₀/2ζ)/k', 'a_rms in g if G in g²/Hz'] },
        ].map(({ x, y, w, h, col, bg, bc, title, lines }) => (
          <g key={x}>
            <rect x={x} y={y} width={w} height={h} rx={4} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={x + 6} y={y + 16} fontSize="10" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={x + 6} y={y + 32 + li * 15} fontSize="9.5" fill="#333" fontFamily="'Courier New',monospace">{line}</text>
            ))}
          </g>
        ))}
        {/* Arrows between steps */}
        {[142, 306, 480].map(x => (
          <line key={x} x1={x} y1={129} x2={x + 8} y2={129} stroke="#c0392b" strokeWidth="2" markerEnd="url(#me1)" />
        ))}

        {/* Right panel: sensitivity table */}
        <rect x={584} y={10} width={304} height={H - 18} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={736} y={28} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Sensitivity: x_rms ∝ ?</text>

        {[
          { param: 'fₙ doubled', effect: 'x_rms increases by √2', col: '#c0392b', dir: '↑' },
          { param: 'ζ halved', effect: 'x_rms increases by √2', col: '#c0392b', dir: '↑' },
          { param: 'S₀ doubled', effect: 'x_rms increases by √2', col: '#c0392b', dir: '↑' },
          { param: 'fₙ halved', effect: 'x_rms decreases by √2', col: '#27ae60', dir: '↓' },
          { param: 'ζ doubled', effect: 'x_rms decreases by √2', col: '#27ae60', dir: '↓' },
          { param: 'All × 4', effect: 'x_rms × √4 = × 2', col: '#2E75B6', dir: '→' },
        ].map(({ param, effect, col, dir }, i) => {
          const y = 38 + i * 38;
          const bg = i % 2 === 0 ? '#fff5f5' : '#fff';
          return (
            <g key={i}>
              <rect x={590} y={y} width={292} height={34} fill={bg} stroke="#f0cece" strokeWidth="0.5" />
              <text x={596} y={y + 22} fontSize="10" fill="#7B0D0D" fontWeight="700" fontFamily="'Courier New',monospace">{param}</text>
              <text x={720} y={y + 22} fontSize="10" fill={col}>{effect}</text>
              <text x={870} y={y + 22} fontSize="14" fill={col} fontWeight="700">{dir}</text>
            </g>
          );
        })}

        {/* Worked example */}
        <rect x={10} y={168} width={560} height={84} rx={4} fill="#fff" stroke="#f0cece" />
        <text x={290} y={184} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Worked Example: Printed Circuit Board Vibration</text>
        {[
          'fₙ = 200 Hz,  ζ = 0.05,  G_in = 0.02 g²/Hz (flat PSD)',
          'a_rms = √(π × 200 × 0.02 / (2 × 0.05)) = √(125.66) = 11.2 g_rms',
          '3σ = 33.6 g  (99.7% of peaks below this level — design limit)',
          'Peak factor k_p ≈ √(2 ln(fₙ T)) for expected max in observation time T',
        ].map((line, li) => (
          <text key={li} x={18} y={198 + li * 14} fontSize="9.5" fill={li === 1 ? '#c0392b' : '#333'}
            fontFamily="'Courier New',monospace" fontWeight={li === 1 ? '700' : '400'}>{line}</text>
        ))}

        {/* Validity note */}
        <rect x={10} y={260} width={560} height={50} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
        <text x={18} y={276} fontSize="10" fill="#7B0D0D" fontWeight="700">Miles validity conditions:</text>
        <text x={18} y={290} fontSize="9.5" fill="#333">1. Input PSD is flat (white noise) over bandwidth containing the resonance (±2–3 octaves around fₙ)</text>
        <text x={18} y={304} fontSize="9.5" fill="#333">2. System is lightly damped (ζ &lt; 0.2) and single-mode dominant.  Fails for multiple close modes.</text>
      </svg>
      <div style={S.dCaption}>Miles' equation is the most widely used formula in aerospace and electronics random vibration design. It gives the RMS response of a single-degree-of-freedom system to a white (flat PSD) broadband random input. The formula arises because the response is dominated by the resonance peak, and the area under |H(f)|² for a lightly damped SDOF integrates analytically to πfₙ/(2ζ). The RMS response scales as the square root of the product of natural frequency, input PSD level, and 1/(2ζ) — emphasising that low damping and high natural frequency both increase dynamic loads.</div>
    </div>
  );
}

// ── Diagram 6: MDOF Random Response and Cross-Spectral Density ────
function DiagramMDOF() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>MDOF Systems — Modal Superposition and Cross-Spectral Density Matrix</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="mf1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Flow diagram: MDOF analysis procedure */}
        {(()=>{
          const steps = [
            { x:10,  label:'[M],[K],[C]\nMatrices', col:'#c0392b', bg:'#FEF0F0', bc:'#c0392b' },
            { x:160, label:'Modal\nAnalysis', col:'#2E75B6', bg:'#eaf3ff', bc:'#2E75B6' },
            { x:310, label:'Modes\nφᵣ, ωᵣ, ζᵣ', col:'#27ae60', bg:'#f0fff4', bc:'#27ae60' },
            { x:460, label:'Modal\nTransfer Hᵣ(f)', col:'#e07b00', bg:'#fff9e6', bc:'#e07b00' },
            { x:610, label:'S_qq(f)\nModal PSD', col:'#9b59b6', bg:'#f8f0ff', bc:'#9b59b6' },
            { x:760, label:'S_xx(f)\nPhysical PSD', col:'#c0392b', bg:'#FEF0F0', bc:'#c0392b' },
          ];
          return steps.map(({ x, label, col, bg, bc }, i) => (
            <g key={i}>
              <rect x={x} y={10} width={140} height={50} rx={5} fill={bg} stroke={bc} strokeWidth="2" />
              {label.split('\n').map((line, li) => (
                <text key={li} x={x + 70} y={30 + li * 16} textAnchor="middle" fontSize="10.5" fill={col} fontWeight="700">{line}</text>
              ))}
              {i < steps.length - 1 && (
                <line x1={x + 140} y1={35} x2={x + 158} y2={35} stroke="#c0392b" strokeWidth="2" markerEnd="url(#mf1)" />
              )}
            </g>
          ));
        })()}

        {/* Cross-spectral density explanation */}
        <rect x={10} y={72} width={430} height={130} rx={4} fill="#fff" stroke="#f0cece" />
        <text x={225} y={90} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Cross-Spectral Density Matrix [S_xx(f)]</text>
        {[
          ['Diagonal: S_xi_xi(f)', 'Auto-PSD of DOF i — real, non-negative'],
          ['Off-diagonal: S_xi_xj(f)', 'Cross-PSD between DOFs i and j — complex'],
          ['Phase: arg(S_xi_xj)', 'Phase lag between motion at i and j'],
          ['Coherence: γ²_ij(f)', '|S_ij|² / (S_ii·S_jj) ∈ [0,1]'],
          ['Matrix property:', '[S_xx] = [H][S_FF][H]ᴴ (H = FRF matrix)'],
          ['σ²_xi = ∫S_xi_xi df', 'Mean square displacement at DOF i'],
        ].map(([key, val], i) => {
          const rowY = 100 + i * 16;
          const bg = i % 2 === 0 ? '#fff5f5' : '#fff';
          return (
            <g key={i}>
              <rect x={12} y={rowY - 10} width={428} height={16} fill={bg} />
              <text x={18} y={rowY + 2} fontSize="9.5" fill="#7B0D0D" fontWeight="700" fontFamily="'Courier New',monospace">{key}</text>
              <text x={190} y={rowY + 2} fontSize="9.5" fill="#333">{val}</text>
            </g>
          );
        })}

        {/* Modal superposition formula */}
        <rect x={450} y={72} width={438} height={130} rx={4} fill="#fff" stroke="#f0cece" />
        <text x={669} y={90} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Modal Superposition for Random Response</text>
        {[
          'Full response PSD:',
          '  [S_xx(f)] = Σᵣ Σₛ φᵣ φₛᵀ Hᵣ(f)Hₛ*(f) S_qqᵣₛ(f)',
          '',
          'Uncorrelated modes (diagonal S_qq):',
          '  [S_xx(f)] = Σᵣ |Hᵣ(f)|² Sᵣ(f) [φᵣφᵣᵀ]',
          '',
          'σ²_xi = Σᵣ φᵣᵢ² ∫|Hᵣ(f)|² Sᵣ(f) df  (SRSS rule)',
          '  → Square Root of Sum of Squares approximation',
        ].map((line, li) => (
          <text key={li} x={458} y={106 + li * 13} fontSize="9.5" fill={line.includes('SRSS') ? '#c0392b' : '#333'}
            fontFamily="'Courier New',monospace" fontWeight={line.includes('SRSS') ? '700' : '400'}>{line}</text>
        ))}

        {/* Combining modes */}
        <rect x={10} y={212} width={880} height={90} rx={4} fill="#fff" stroke="#f0cece" />
        <text x={450} y={228} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Modal Combination Methods</text>

        {[
          { x: 16,  col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'SRSS (Square Root Sum Squares)',
            lines: ['σ_total = √(Σσᵣ²)', 'Valid: widely-spaced modes', 'Standard in structural codes'] },
          { x: 236, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'CQC (Complete Quadratic Combin.)',
            lines: ['σ = √(Σᵢ Σⱼ ρᵢⱼ σᵢ σⱼ)', 'ρᵢⱼ = correlation coefficient', 'Accounts for mode coupling'] },
          { x: 456, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Direct Integration',
            lines: ['Newmark-β with random forces', 'Exact but computationally heavy', 'Required for nonlinear systems'] },
          { x: 676, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
            title: 'Covariance Method',
            lines: ['Lyapunov equation for covariance', 'P = ∫S_xx(f)df (full matrix)', 'Efficient for state-space models'] },
        ].map(({ x, col, bg, bc, title, lines }) => (
          <g key={x}>
            <rect x={x} y={234} width={212} height={62} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={x + 6} y={248} fontSize="10" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={x + 6} y={262 + li * 13} fontSize="9" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>MDOF random vibration analysis proceeds by modal decomposition: the system is first diagonalised into uncoupled modal oscillators, each with its own transfer function. The physical response PSD matrix is reassembled as a sum over all mode contributions. When modal frequencies are well-separated, the SRSS (square root of sum of squares) approximation is adequate. When modes are closely spaced, the Complete Quadratic Combination (CQC) method accounts for inter-modal correlation. The cross-spectral density matrix captures not just response amplitudes but also phase relationships between points — essential for structural health monitoring and fatigue correlation.</div>
    </div>
  );
}

// ── Diagram 7: Fatigue and Damage under Random Loading ────────────
function DiagramFatigue() {
  const W = 900, H = 330;
  const PW = (W - 30) / 2;

  // Rayleigh distribution of peaks (narrow-band)
  const N = 150;
  const sigmaX = 1.0;
  const aVals = Array.from({ length: N }, (_, i) => i / (N - 1) * 4 * sigmaX);
  const rayleigh = aVals.map(a => (a / sigmaX ** 2) * Math.exp(-(a ** 2) / (2 * sigmaX ** 2)));

  // S-N curve (Basquin)
  const nPts = 100;
  const NcVals = Array.from({ length: nPts }, (_, i) => Math.pow(10, 3 + i / (nPts - 1) * 5));
  const b = 0.1, Su = 5.0;
  const Sn = NcVals.map(N => Su * Math.pow(N, -b));

  const ox1 = 14, oy1 = 220, ow1 = PW - 28, oh1 = 170;
  const toXa = a => ox1 + (a / (4 * sigmaX)) * ow1;
  const toYp = p => oy1 - Math.min(p / 0.65, 1) * oh1;

  const ox2 = PW + 36, oy2 = 260, ow2 = PW - 50, oh2 = 200;
  const toXN = N => ox2 + Math.log10(N / 1e3) / 5 * ow2;
  const toYS = Sv => oy2 - Math.min(Sv / Su, 1) * oh2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Fatigue Damage under Random Loading — Peak Distribution and Miner's Rule</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="fat1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Peak amplitude distribution */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Peak Amplitude Distribution — Narrow-Band Random</text>
        <text x={10 + PW / 2} y={42} textAnchor="middle" fontSize="10" fill="#555">Rayleigh PDF: p(a) = (a/σ²)·exp(−a²/2σ²)</text>

        {/* Rayleigh PDF */}
        <polyline
          points={aVals.map((a, i) => `${toXa(a)},${toYp(rayleigh[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />
        <polygon
          points={`${toXa(0)},${oy1} ` + aVals.map((a, i) => `${toXa(a)},${toYp(rayleigh[i])}`).join(' ') + ` ${toXa(4 * sigmaX)},${oy1}`}
          fill="#c0392b" opacity="0.07"
        />

        {/* Key statistics */}
        {[
          { a: sigmaX, label: 'σ (1σ)', col: '#c0392b', pct: '39.3%' },
          { a: 2 * sigmaX, label: '2σ', col: '#e07b00', pct: '86.5%' },
          { a: 3 * sigmaX, label: '3σ', col: '#2E75B6', pct: '98.9%' },
        ].map(({ a, label, col, pct }) => {
          const x = toXa(a);
          const idx = Math.round(a / (4 * sigmaX) * (N - 1));
          const y = toYp(rayleigh[idx]);
          return (
            <g key={a}>
              <line x1={x} y1={oy1} x2={x} y2={y - 2} stroke={col} strokeWidth="1.2" strokeDasharray="4,2" />
              <text x={x} y={y - 4} textAnchor="middle" fontSize="9" fill={col} fontWeight="700">{label}</text>
              <text x={x} y={oy1 + 14} textAnchor="middle" fontSize="8.5" fill={col}>{pct}</text>
            </g>
          );
        })}

        {/* Axes */}
        <line x1={ox1} y1={oy1 + 4} x2={ox1 + ow1 + 8} y2={oy1 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#fat1)" />
        <line x1={ox1} y1={oy1 + 4} x2={ox1} y2={oy1 - oh1 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#fat1)" />
        <text x={ox1 + ow1 + 12} y={oy1 + 8} fontSize="10" fill="#555">a</text>
        <text x={ox1 + 4} y={oy1 - oh1 - 10} fontSize="10" fill="#555">p(a)</text>

        {/* Damage formula */}
        <rect x={14} y={H - 66} width={PW - 20} height={50} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={20} y={H - 52} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Narrow-band fatigue damage rate (Dirlik):</text>
        <text x={20} y={H - 38} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">D = N_peaks/T · ∫ p(a)/N(a) da</text>
        <text x={20} y={H - 24} fontSize="9.5" fill="#333">N(a) from S-N curve;  N_peaks/T = fₙ (for narrow-band)</text>

        {/* Panel 2: S-N curve and Miner's rule */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">S-N Curve (Basquin) and Miner's Cumulative Damage</text>

        {/* S-N curve */}
        <polyline
          points={NcVals.map((Nc, i) => `${toXN(Nc)},${toYS(Sn[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />

        {/* Endurance limit */}
        {(() => {
          const Se = 1.2;
          return (
            <g>
              <line x1={ox2} y1={toYS(Se)} x2={ox2 + ow2} y2={toYS(Se)} stroke="#c0392b" strokeWidth="1.2" strokeDasharray="5,3" opacity="0.6" />
              <text x={ox2 + ow2 - 4} y={toYS(Se) - 3} textAnchor="end" fontSize="9.5" fill="#c0392b">Sₑ (endurance limit)</text>
            </g>
          );
        })()}

        {/* Sample damage cycles */}
        {[
          { Nc: 1e4, S: 3.0, n: 1000, col: '#c0392b' },
          { Nc: 1e5, S: 2.0, n: 10000, col: '#e07b00' },
          { Nc: 1e6, S: 1.5, n: 50000, col: '#2E75B6' },
        ].map(({ Nc, S, n, col }) => {
          const x = toXN(Nc), y = toYS(S);
          const ratio = (n / Nc).toFixed(4);
          return (
            <g key={Nc}>
              <circle cx={x} cy={y} r={4} fill={col} />
              <text x={x + 5} y={y - 2} fontSize="8.5" fill={col}>n={n}, N={Nc.toExponential(0)}</text>
              <text x={x + 5} y={y + 10} fontSize="8.5" fill={col}>n/N={ratio}</text>
            </g>
          );
        })}

        {/* Miner's rule box */}
        <rect x={PW + 26} y={H - 80} width={PW - 14} height={64} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={PW + 32} y={H - 66} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Miner's Cumulative Damage Rule:</text>
        <text x={PW + 32} y={H - 52} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">D = Σᵢ (nᵢ / Nᵢ)  — failure when D = 1</text>
        <text x={PW + 32} y={H - 38} fontSize="9.5" fill="#333">nᵢ = applied cycles at stress Sᵢ</text>
        <text x={PW + 32} y={H - 24} fontSize="9.5" fill="#333">Nᵢ = cycles to failure at Sᵢ from S-N curve</text>

        {/* Axes */}
        <line x1={ox2} y1={oy2 + 4} x2={ox2 + ow2 + 8} y2={oy2 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#fat1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={oy2 - oh2 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#fat1)" />
        <text x={ox2 + ow2 + 12} y={oy2 + 8} fontSize="10" fill="#555">N (cycles)</text>
        <text x={ox2 + 4} y={oy2 - oh2 - 10} fontSize="10" fill="#555">S</text>
        {[3, 4, 5, 6, 7, 8].map(e => {
          const x = toXN(Math.pow(10, e));
          return (
            <g key={e}>
              <text x={x} y={oy2 + 15} textAnchor="middle" fontSize="9" fill="#aaa">10^{e}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Under random loading, fatigue damage accumulates from many cycles at varying stress amplitudes. For narrow-band processes, peak amplitudes follow a Rayleigh distribution — about 1% of peaks exceed 3σ, but in millions of cycles these rare events dominate fatigue damage. Miner's rule D = Σ(nᵢ/Nᵢ) linearly accumulates fractional damage at each stress level. The Dirlik method (empirical but accurate) integrates the Rayleigh distribution over the S-N curve to give the expected damage rate directly from the PSD. Failure is predicted when D = 1, though in practice a factor of safety D_allowable = 0.3–0.5 is used.</div>
    </div>
  );
}

// ── Diagram 8: Random Vibration Testing Standards ─────────────────
function DiagramTestingStandards() {
  const W = 900, H = 290;

  // Typical aerospace PSD profile (MIL-STD-810 / GEVS)
  const profilePts = [
    [20,   0.0053],
    [80,   0.04],
    [350,  0.04],
    [2000, 0.0036],
  ];

  const nF = 200;
  const fVals = Array.from({ length: nF }, (_, i) => 20 + i / (nF - 1) * (2000 - 20));
  const profilePSD = fVals.map(f => {
    if (f <= 20)   return 0.0053;
    if (f <= 80)   return 0.0053 * Math.pow(f / 20, 3);
    if (f <= 350)  return 0.04;
    if (f <= 2000) return 0.04 * Math.pow(350 / f, 1.667);
    return 0;
  });

  // Compute total grms
  const df = (2000 - 20) / (nF - 1);
  const grms = Math.sqrt(profilePSD.reduce((a, b) => a + b, 0) * df);

  const ox = 14, oy = 200, ow = 520, oh = 170;
  const toXf = f => ox + Math.log10(f / 20) / Math.log10(2000 / 20) * ow;
  const toYG = g => oy - Math.min(Math.log10(g / 0.001) / Math.log10(0.1 / 0.001), 1) * oh;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Random Vibration Test Profile — Aerospace PSD Specification (GEVS / MIL-STD-810)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="ts1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Profile plot */}
        <rect x={10} y={10} width={560} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={290} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Typical Aerospace Random Vibration Test PSD</text>

        {/* Grid */}
        {[0.001, 0.003, 0.01, 0.03, 0.1].map(g => {
          const y = toYG(g);
          if (y < 10 || y > oy + 4) return null;
          return (
            <g key={g}>
              <line x1={ox} y1={y} x2={ox + ow} y2={y} stroke="#f5f5f5" strokeWidth="1" />
              <text x={ox - 4} y={y + 4} textAnchor="end" fontSize="8.5" fill="#aaa">{g}</text>
            </g>
          );
        })}
        {[20, 50, 100, 200, 500, 1000, 2000].map(f => {
          const x = toXf(f);
          if (x < ox || x > ox + ow) return null;
          return (
            <g key={f}>
              <line x1={x} y1={10} x2={x} y2={oy} stroke="#f5f5f5" strokeWidth="1" />
              <text x={x} y={oy + 12} textAnchor="middle" fontSize="8.5" fill="#aaa">{f}</text>
            </g>
          );
        })}

        {/* Profile fill */}
        <polygon
          points={`${toXf(20)},${oy} ` + fVals.map((f, i) => `${toXf(f)},${toYG(profilePSD[i])}`).join(' ') + ` ${toXf(2000)},${oy}`}
          fill="#c0392b" opacity="0.08"
        />

        {/* Profile line */}
        <polyline
          points={fVals.map((f, i) => `${toXf(f)},${toYG(profilePSD[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />

        {/* Breakpoint markers */}
        {profilePts.map(([f, g]) => (
          <g key={f}>
            <circle cx={toXf(f)} cy={toYG(g)} r={4} fill="#c0392b" />
            <text x={toXf(f) + 4} y={toYG(g) - 4} fontSize="8.5" fill="#c0392b" fontWeight="700">({f},{g})</text>
          </g>
        ))}

        {/* Slope annotations */}
        <text x={toXf(40)} y={toYG(0.012) - 8} fontSize="9" fill="#e07b00" fontStyle="italic">+3 dB/oct</text>
        <text x={toXf(800)} y={toYG(0.018) + 14} fontSize="9" fill="#e07b00" fontStyle="italic">-5 dB/oct</text>

        {/* grms label */}
        <text x={ox + ow - 4} y={oy - oh + 18} textAnchor="end" fontSize="10.5" fill="#c0392b" fontWeight="700">Overall = {grms.toFixed(2)} g_rms</text>

        {/* Axes */}
        <line x1={ox} y1={10} x2={ox} y2={oy + 4} stroke="#888" strokeWidth="1.5" />
        <line x1={ox} y1={oy} x2={ox + ow + 8} y2={oy} stroke="#888" strokeWidth="1.5" markerEnd="url(#ts1)" />
        <text x={ox + ow / 2} y={oy + 24} textAnchor="middle" fontSize="10" fill="#555">Frequency (Hz) — log scale</text>
        <text x={ox - 28} y={oy - oh / 2} fontSize="9.5" fill="#555" transform={`rotate(-90,${ox - 28},${oy - oh / 2})`} textAnchor="middle">PSD (g²/Hz) — log scale</text>

        {/* Right panel: standards reference */}
        <rect x={584} y={10} width={304} height={H - 18} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={736} y={28} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Standards Reference</text>

        {[
          { y: 38, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'MIL-STD-810',
            lines: ['US military equipment testing', 'Method 514.8: vibration', 'Defines test duration + PSD levels', 'Based on measured field environments'] },
          { y: 120, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'NASA-GEVS-7000',
            lines: ['Goddard Space Flight Center', 'Minimum workmanship test: 14.1 g_rms', 'Mission-specific: add mission PSD', 'Duration: 3 min per axis (workmanship)'] },
          { y: 202, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'IEC 60068-2-64',
            lines: ['International electronics test', 'Specifies PSD profile + duration', 'Random vs. sinusoidal test selection', 'Used for consumer/industrial electronics'] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={590} y={y} width={292} height={lines.length * 16 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={596} y={y + 15} fontSize="10.5" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={596} y={y + 30 + li * 16} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Random vibration test profiles specify how much vibration energy exists at each frequency. The log-log plot shows the characteristic shape: rising with slope +3 or +6 dB/octave at low frequencies, a flat plateau in the mid-range, and rolling off at high frequencies. The overall g_rms is the square root of the area under the PSD. Tests are specified by industry standards (MIL-STD-810 for military, GEVS for NASA spacecraft, IEC 60068 for electronics) and are designed to reproduce the expected lifetime vibration environment in a compressed duration — typically 1–3 hours per axis representing years of field service.</div>
    </div>
  );
}

// ── Diagram 9: Non-Gaussian and Extreme Value Statistics ──────────
function DiagramExtremeValue() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  // Gaussian vs high-kurtosis PDF comparison
  const N = 200;
  const xVals = Array.from({ length: N }, (_, i) => -5 + i / (N - 1) * 10);
  const gaussian = xVals.map(x => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI));
  const leptokurtic = xVals.map(x => {
    // Laplace distribution (high kurtosis = 6)
    const a = 0.7;
    return (1 / (2 * a)) * Math.exp(-Math.abs(x) / a) * 0.7 + gaussian[Math.round((x + 5) / 10 * (N - 1))] * 0.3;
  });

  // Extreme value (Gumbel) distribution
  const tVals = Array.from({ length: 150 }, (_, i) => -2 + i / 149 * 10);
  const mu_g = 2.5, beta_g = 0.8;
  const gumbel = tVals.map(t => {
    const z = (t - mu_g) / beta_g;
    return (1 / beta_g) * Math.exp(-(z + Math.exp(-z)));
  });

  const ox = 14, oy = 200, ow = PW - 28, oh = 160;
  const toX = x => ox + (x + 5) / 10 * ow;
  const toY = p => oy - Math.min(p / 0.65, 1) * oh;

  const ox2 = PW + 36, oy2 = 240, ow2 = PW - 50, oh2 = 190;
  const toXg = t => ox2 + (t + 2) / 12 * ow2;
  const toYg = p => oy2 - Math.min(p / 0.55, 1) * oh2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Non-Gaussian Processes and Extreme Value Statistics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="ev1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Gaussian vs non-Gaussian PDF */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Gaussian vs. High-Kurtosis (Leptokurtic) PDF</text>
        <text x={10 + PW / 2} y={42} textAnchor="middle" fontSize="10" fill="#555">High kurtosis: heavier tails → more extreme excursions</text>

        {/* Gaussian */}
        <polyline
          points={xVals.map((x, i) => `${toX(x)},${toY(gaussian[i])}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2.5"
        />

        {/* Leptokurtic */}
        <polyline
          points={xVals.map((x, i) => `${toX(x)},${toY(leptokurtic[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />

        {/* Tail comparison */}
        {(() => {
          const tailX = 3.0;
          const x = toX(tailX);
          const yG = toY(gaussian[Math.round((tailX + 5) / 10 * (N - 1))]);
          const yL = toY(leptokurtic[Math.round((tailX + 5) / 10 * (N - 1))]);
          return (
            <g>
              <line x1={x} y1={Math.min(yG, yL) - 4} x2={x} y2={Math.max(yG, yL) + 4} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="3,2" />
              <text x={x + 4} y={(yG + yL) / 2} fontSize="9" fill="#e07b00" fontWeight="700">Heavier tail</text>
            </g>
          );
        })()}

        {/* Legend */}
        <line x1={ox + 8} y1={H - 60} x2={ox + 28} y2={H - 60} stroke="#2E75B6" strokeWidth="2.5" />
        <text x={ox + 32} y={H - 56} fontSize="9.5" fill="#2E75B6">Gaussian (γ₂ = 3)</text>
        <line x1={ox + 8} y1={H - 44} x2={ox + 28} y2={H - 44} stroke="#c0392b" strokeWidth="2.5" />
        <text x={ox + 32} y={H - 40} fontSize="9.5" fill="#c0392b">Leptokurtic (γ₂ &gt; 3)</text>

        {/* Kurtosis cards */}
        <rect x={14} y={H - 28} width={PW - 20} height={16} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
        <text x={20} y={H - 16} fontSize="9.5" fill="#7B0D0D" fontWeight="700">Impact, clipped random (γ₂ &lt; 3 platykurtic), nonlinear processes → non-Gaussian; use kurtosis control in testing</text>

        {/* Axes */}
        <line x1={ox} y1={oy + 4} x2={ox + ow + 8} y2={oy + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#ev1)" />
        <line x1={ox} y1={oy + 4} x2={ox} y2={oy - oh - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#ev1)" />
        <text x={ox + ow + 12} y={oy + 8} fontSize="10" fill="#555">x</text>
        {[-4, -2, 0, 2, 4].map(xv => (
          <text key={xv} x={toX(xv)} y={oy + 14} textAnchor="middle" fontSize="9" fill="#aaa">{xv}</text>
        ))}

        {/* Panel 2: Extreme value and peak factor */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Extreme Value Distribution and Peak Factor</text>

        {/* Gumbel distribution */}
        <polyline
          points={tVals.map((t, i) => `${toXg(t)},${toYg(gumbel[i])}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.5"
        />
        <polygon
          points={`${toXg(tVals[0])},${oy2} ` + tVals.map((t, i) => `${toXg(t)},${toYg(gumbel[i])}`).join(' ') + ` ${toXg(tVals[tVals.length - 1])},${oy2}`}
          fill="#c0392b" opacity="0.07"
        />

        {/* Mode and mean */}
        <line x1={toXg(mu_g)} y1={oy2} x2={toXg(mu_g)} y2={toYg(Math.max(...gumbel)) - 4} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,2" />
        <text x={toXg(mu_g) + 4} y={toYg(Math.max(...gumbel)) - 6} fontSize="9.5" fill="#c0392b" fontWeight="700">Mode (most likely max)</text>

        {/* Axes */}
        <line x1={ox2} y1={oy2 + 4} x2={ox2 + ow2 + 8} y2={oy2 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#ev1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={oy2 - oh2 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#ev1)" />
        <text x={ox2 + ow2 + 12} y={oy2 + 8} fontSize="10" fill="#555">x_max</text>
        <text x={ox2 + 4} y={oy2 - oh2 - 10} fontSize="10" fill="#555">p</text>

        {/* Peak factor formulas */}
        {[
          { y: 44, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Peak Factor k_p (Davenport)',
            lines: ['k_p = √(2 ln(N_peaks))', 'N_peaks = fₙ · T (peaks in T seconds)', 'For T=1hr, fₙ=10Hz: N=36000 → k_p≈4.0', 'x_max = k_p · σ  (design limit)'] },
          { y: 144, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Extreme Value Theory (Gumbel)',
            lines: ['P(x_max < u) = exp(-exp(-(u-μ)/β))', 'μ = mode,  β = scale parameter', 'E[x_max] = μ + 0.5772·β (Euler constant)', 'P(exceedance) = 1 - exp(-exp(-(u-μ)/β))'] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={PW + 26} y={y} width={PW - 14} height={lines.length * 17 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={PW + 32} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={PW + 32} y={y + 30 + li * 17} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Real vibration environments are often non-Gaussian — rocket shock, road impacts, and rotating machinery all produce distributions with heavier tails than Gaussian (high kurtosis). Heavy-tailed distributions increase the probability of large excursions beyond 3σ, leading to under-prediction of fatigue damage by Gaussian methods. The extreme value (Gumbel) distribution gives the probability distribution of the maximum response over an observation period. The peak factor k_p = √(2 ln(N_peaks)) predicts the expected maximum response as a multiple of the RMS — typically 3σ–4σ for engineering design limits.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function RandomVibrations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Random Vibrations</h1>
      <p style={S.p}>
        Random vibration is the study of mechanical systems subjected to excitation that cannot be described by a deterministic function of time. Wind loading on a building, road roughness beneath a vehicle, acoustic pressure on a rocket payload, ocean waves on an offshore platform — all are random processes characterised statistically rather than by a precise waveform. The governing framework is probability theory and spectral analysis: instead of asking "what is the response at time t?", we ask "what is the probability distribution of the response, and how much energy exists at each frequency?" The answers determine fatigue life, structural reliability, and test requirements for almost every engineering structure that operates in the natural environment.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "A random process is characterised by its mean μ, variance σ², and autocorrelation R(τ) — not by a specific waveform",
        "Wiener-Khinchin: S(f) = FT{R(τ)} — power spectral density and autocorrelation are a Fourier transform pair",
        "Input-output relation: S_XX(f) = |H(f)|² · S_FF(f) — output PSD = squared FRF times input PSD",
        "Miles' equation: x_rms = √(π·fₙ·S₀/(2ζ)) — RMS response of SDOF to white noise input",
        "Stationarity: statistics constant in time — required for PSD analysis to be well-defined",
        "Ergodicity: time average = ensemble average — allows single record to characterise the process",
        "Gaussian process: fully described by mean and PSD; preserved through linear systems",
        "Narrow-band peaks follow Rayleigh distribution; expected maximum over T seconds: x_max ≈ k_p·σ",
        "Miner's rule: cumulative fatigue damage D = Σ(nᵢ/Nᵢ); failure when D = 1",
        "SRSS combination: σ_total = √(Σσᵣ²) for well-separated modes under random excitation",
      ]} />

      <h2 style={S.h2}>Random Processes — Time History and Statistics</h2>
      <p style={S.p}>
        A random process assigns a time history to each outcome of a random experiment. At any fixed time, the value is a random variable with a probability distribution. The ensemble is the complete collection of all possible time histories; a stationary ergodic process can be characterised by a single long time record because its time statistics equal its ensemble statistics.
      </p>
      <DiagramRandomProcess />
      <Table2 rows={[
        ["Random process X(t,θ)", "Assigns a time function x(t) to each sample outcome θ\nAt fixed t: X(t) is a random variable with CDF F(x,t) = P(X(t) ≤ x)\nAt fixed θ: a single realisation (sample function, time history)"],
        ["Mean (expected value)", "μ_X(t) = E[X(t)] = ∫ x·p(x,t) dx\nFor stationary process: μ_X = const (independent of t)\nEstimated from time record: μ̂ = (1/T)∫x(t)dt  (ergodic assumption)"],
        ["Variance and RMS", "σ²_X = E[(X−μ)²] = E[X²] − μ²\nRMS = √E[X²] = √(σ² + μ²)\nFor zero-mean: RMS = σ\nUnits: same as the signal (m, m/s, m/s², Pa, N, ...)"],
        ["Gaussian (Normal) distribution", "p(x) = (1/σ√2π) exp(−(x−μ)²/(2σ²))\n68.3% of values within ±σ;  95.4% within ±2σ;  99.7% within ±3σ\nDesign limit: ±3σ for 0.3% exceedance probability"],
        ["Kurtosis (4th moment)", "γ₂ = E[(x−μ)⁴]/σ⁴\nGaussian: γ₂ = 3 (reference value)\nγ₂ > 3 (leptokurtic): heavier tails — more extreme events\nγ₂ < 3 (platykurtic): lighter tails — clipped or limited-amplitude signal"],
        ["Central limit theorem", "Sum of n independent random variables → Gaussian as n → ∞\nJustifies Gaussian model when many independent sources combine\nConvergence rate: σ²_sum = Σσᵢ² (even if individual distributions are non-Gaussian)"],
      ]} />

      <h2 style={S.h2}>Autocorrelation and Power Spectral Density</h2>
      <p style={S.p}>
        The autocorrelation function R(τ) and the power spectral density S(f) are the two fundamental descriptors of a stationary random process. They encode identical information in complementary domains — R(τ) in the time lag domain, S(f) in the frequency domain — and are related by the Wiener-Khinchin theorem.
      </p>
      <DiagramPSD />
      <Table2 rows={[
        ["Autocorrelation R(τ)", "R(τ) = E[X(t)·X(t+τ)]\nR(0) = σ² (variance — maximum value)\nR(τ) = R(−τ) (even function)\nR(τ) → 0 as τ → ∞ for ergodic processes"],
        ["Wiener-Khinchin theorem", "S(f) = ∫_{-∞}^{∞} R(τ)·e^{−j2πfτ} dτ\nR(τ) = ∫_{-∞}^{∞} S(f)·e^{+j2πfτ} df\nS(f) ≥ 0 always (non-negative real)\nσ² = R(0) = ∫S(f)df  (Parseval's theorem)"],
        ["White noise", "S(f) = S₀ = const for all f\nR(τ) = S₀·δ(τ) — delta function (instantaneous decorrelation)\nIdealisation: infinite bandwidth, infinite variance\nBand-limited white: S = S₀ for f₁ ≤ f ≤ f₂, zero elsewhere"],
        ["Coloured noise", "PSD with frequency-shaped content\nPink noise: S ∝ 1/f (equal energy per octave)\nRed (Brownian) noise: S ∝ 1/f²  (integral of white noise)\nBlue noise: S ∝ f;  Violet: S ∝ f²"],
        ["Correlation length τ_c", "Characteristic time beyond which signal is decorrelated\nτ_c = ∫R(τ)/R(0) dτ  (integral time scale)\nRelated to bandwidth: τ_c ≈ 1/Δf where Δf is bandwidth\nNarrow-band: long τ_c (almost periodic); wide-band: short τ_c"],
        ["Cross-correlation & cross-PSD", "R_XY(τ) = E[X(t)·Y(t+τ)]  (between two signals)\nS_XY(f) = FT{R_XY(τ)}  (complex — has phase)\nCoherence: γ²_XY(f) = |S_XY|²/(S_XX·S_YY) ∈ [0,1]\nγ² = 1: perfectly linearly related; γ² = 0: uncorrelated"],
      ]} />

      <h2 style={S.h2}>SDOF Response to Random Excitation</h2>
      <p style={S.p}>
        The response of a linear SDOF system to random excitation is determined entirely by the transfer function H(f) and the input PSD S_FF(f). The key relationship S_XX = |H|²·S_FF transforms a probability/spectral description of the input into a complete statistical description of the output.
      </p>
      <DiagramSDOFRandom />
      <Table2 rows={[
        ["Spectral input-output law", "S_XX(f) = |H(f)|²·S_FF(f)\nH(f) = 1/(m(ωₙ²−ω²+2jζωₙω)) — SDOF receptance TF\nMean square response: σ²_X = ∫|H(f)|² S_FF(f) df"],
        ["White noise SDOF result", "For S_FF = S₀ (flat):\nσ²_X = S₀·∫|H(f)|² df = S₀·π/(4ζωₙ³m²)\nσ_X = √(πS₀/(4ζωₙ³m²)) — closed-form RMS"],
        ["Miles' equation (acceleration)", "a_rms = √(π·fₙ·G_in/(2ζ))\nG_in = one-sided input acceleration PSD [g²/Hz]\nAssumes white noise input over resonance bandwidth\nMost widely used formula in aerospace random vibration"],
        ["Half-power bandwidth", "B_hp = 2ζfₙ (Hz) — bandwidth between −3 dB points\nEnergy concentrated in bandwidth B_hp around fₙ\nNarrow band (small ζ) → high peak PSD but small bandwidth\nProduct: peak PSD × bandwidth ≈ total area (variance)"],
        ["Effect of multiple modes", "σ²_total = ∫Σᵣ|Hᵣ(f)|² S(f) df\nIf modes well-separated: σ²_total ≈ Σᵣ σ²ᵣ (SRSS)\nIf modes close: cross-terms important — use full S_XX matrix\nDominant mode: the one with largest σᵣ contribution"],
        ["Physical interpretation", "White noise: all frequencies excited equally; resonance filters the input\nLightly damped: sharp peak, large response, long ringing time\nHeavily damped: broad peak, moderate response, fast decay\nDesign lever: increase ζ or shift fₙ out of input bandwidth"],
      ]} />

      <h2 style={S.h2}>Stationarity, Ergodicity, and Process Properties</h2>
      <p style={S.p}>
        The mathematical tools of random vibration analysis — PSD, transfer function, Miles' equation — are only valid under specific conditions: stationarity and (for practical measurement) ergodicity. Identifying whether these conditions hold is a prerequisite for any analysis, not an afterthought.
      </p>
      <DiagramStatProps />
      <Table2 rows={[
        ["Strict stationarity", "All statistical properties invariant under time shift\nP(X(t₁)≤x₁,...,X(tₙ)≤xₙ) = P(X(t₁+τ)≤x₁,...,X(tₙ+τ)≤xₙ)\nVery strong requirement — rarely verified in practice"],
        ["Wide-sense stationarity (WSS)", "Weaker (and sufficient for most engineering):\n1. E[X(t)] = μ = constant\n2. R(t₁,t₂) = R(t₁−t₂) = R(τ) — depends only on lag\nFor Gaussian processes, WSS implies strict stationarity"],
        ["Ergodicity", "Time averages equal ensemble averages (almost surely)\nRequired for practical spectral estimation from a single record\nSufficient condition: ∫|R(τ)|dτ < ∞ (absolutely integrable)\nTest: compare statistics of non-overlapping segments of record"],
        ["Non-stationarity sources", "Transients: startup, shutdown, impact events\nSlowly varying parameters: warming up, wearing out\nAmplitude modulation: time-varying amplitude envelope\nAnalysis: short-time Fourier transform (spectrogram) or wavelet transform"],
        ["Ergodicity tests", "Split record into N segments; compute mean and variance of each\nCompare: F-test for equal variances; t-test for equal means\nRun test: checks for systematic trends in successive values\nKolmogorov-Smirnov: full distribution test across segments"],
        ["Gaussian process advantages", "Fully described by mean and R(τ) — nothing more needed\nLinear operations on Gaussian → Gaussian (closed under FT, convolution)\nMaximum entropy distribution for given σ² — least informative\nLeads to simple analytical results for probabilities and damage"],
      ]} />

      <h2 style={S.h2}>Miles' Equation and Mean-Square Response</h2>
      <p style={S.p}>
        Miles' equation is the workhorse formula of aerospace and electronics random vibration design. It provides the RMS response of a single-degree-of-freedom resonator to a broadband white noise input in closed form, without requiring numerical integration of the PSD.
      </p>
      <DiagramMiles />
      <Table2 rows={[
        ["Miles' equation derivation", "∫|H(f)|² df = π fₙ/(2ζ k²)  (evaluated by residues or tables)\nWith S_FF = S₀: σ²_X = S₀·π fₙ/(2ζ k²)\nFor acceleration: σ²_a = S₀·π fₙ·ωₙ⁴/m²·(πfₙ/(2ζ)) = πfₙS₀/(2ζ)\nFinal: a_rms = √(π·fₙ·G_in/(2ζ))  [g_rms if G_in in g²/Hz]"],
        ["Validity conditions", "Input PSD flat over at least ±2–3 octaves around fₙ\nSingle dominant mode — SDOF approximation valid\nζ < 0.2 (lightly damped — peak dominant)\nFails if: narrow-band input, multiple close modes, large ζ"],
        ["3-sigma design rule", "For Gaussian response: 99.7% of instantaneous values within ±3σ\nDesign stress = 3σ × stress-per-unit-amplitude\nConservative for fatigue (rare peaks dominate damage)\nPeak factor: k_p = 3 to 4 depending on duration and fₙ"],
        ["Absolute acceleration input", "Miles for base excitation: a_abs_rms = √(π·fₙ·Sbase/(2ζ)) · (1+Q²)^(1/2)/Q\nFor Q = 1/(2ζ) >> 1: a_abs_rms ≈ Q·a_input_rms at resonance\nQ = 10 (ζ=0.05): response amplified 10× relative to input"],
        ["Displacement from Miles", "z_rms = a_rms / (2πfₙ)²  (divide by ωₙ²)\nUseful for clearance checks and stroke requirements\nz_rms = √(G_in/(128ζ·π³·fₙ³))  [m_rms if G_in in m²/(s⁴·Hz)]"],
        ["Combining two inputs", "If two uncorrelated inputs: S_total = S₁ + S₂\nσ²_total = σ²₁ + σ²₂\nCombined: a_rms_total = √(a_rms1² + a_rms2²)"],
      ]} />

      <h2 style={S.h2}>MDOF Systems and Modal Combination</h2>
      <p style={S.p}>
        Multi-degree-of-freedom systems under random excitation are analysed by modal decomposition: the physical DOF are transformed to uncoupled modal coordinates, each responding as an independent SDOF oscillator. The physical response PSD is then reassembled from modal contributions.
      </p>
      <DiagramMDOF />
      <Table2 rows={[
        ["Modal transformation", "x = Φq  (physical = mode shape matrix × modal coordinates)\nUncoupled modal equations: q̈ᵣ + 2ζᵣωᵣq̇ᵣ + ωᵣ²qᵣ = φᵣᵀF/mᵣ\nModal PSD: S_qqᵣ(f) = |Hᵣ(f)|²·S_fᵣfᵣ(f)\nmᵣ = φᵣᵀMφᵣ (modal mass); Hᵣ = 1/(mᵣ(ωᵣ²−ω²+2jζᵣωᵣω))"],
        ["Physical response PSD matrix", "[S_xx(f)] = [H(f)]·[S_FF(f)]·[H(f)]ᴴ\n[H(f)] = Σᵣ φᵣφᵣᵀHᵣ(f)/mᵣ  (FRF matrix)\nDiagonal element S_xi_xi = variance PSD at DOF i\nOff-diagonal S_xi_xj = cross-PSD between DOFs i and j"],
        ["SRSS combination rule", "σ_xi² ≈ Σᵣ (φᵣᵢ/mᵣ)²·σᵣ²  (sum of modal variances)\nValid when ωᵣ/ωₛ >> 1 for all r≠s (widely separated modes)\nσ_xi = √(Σᵣ σ_xi_r²)  (scalar SRSS)\nStandard in structural codes (ASCE, Eurocode seismic)"],
        ["CQC combination rule", "σ²_xi = Σᵣ Σₛ ρᵣₛ·σᵣᵢ·σₛᵢ\nρᵣₛ = 8ζ²(1+β)β^(3/2) / ((1−β²)²+4ζ²β(1+β)²)  β=ωₛ/ωᵣ\nRequired when mode spacing/bandwidth ratio < 10\nReduces to SRSS when modes well-separated (ρᵣₛ → 0 for r≠s)"],
        ["Correlated input forces", "Spatial correlation: S_FF matrix (not diagonal)\nExamples: pressure field on aircraft panel, seismic input\nCoherence of inputs affects which modes are excited\nFor single-point input: S_FF collapses to scalar × outer product"],
        ["State-space Lyapunov approach", "ẋ = Ax + Bu;  state covariance P satisfies AP + PAᵀ + BQBᵀ = 0\nQ = input PSD matrix;  P = E[xxᵀ] (output covariance matrix)\nEfficient for correlated inputs and closely-spaced modes\nUsed in optimal control and Kalman filter random vibration analysis"],
      ]} />

      <h2 style={S.h2}>Fatigue and Damage under Random Loading</h2>
      <p style={S.p}>
        Fatigue failure under random loading is the primary durability concern in most engineering applications. The key challenge is that neither the cycle amplitudes nor the sequence of loading events is known deterministically — instead, they must be characterised statistically from the PSD of the stress response.
      </p>
      <DiagramFatigue />
      <Table2 rows={[
        ["Narrow-band peak distribution", "Rayleigh PDF: p(a) = (a/σ²)·exp(−a²/(2σ²))\nValid when PSD is narrow-band (concentrated near fₙ)\nExpected number of peaks per unit time: ν_peak = fₙ\nExpected peak amplitude: E[a] = σ√(π/2) ≈ 1.25σ"],
        ["Wide-band correction", "For wide-band PSD, peaks less regular — some cycles missed\nIrregularity factor: α = ν₀/ν_peak  (zero-crossing rate / peak rate)\nα = 1: narrow-band (all peaks = all zero crossings)\nα < 1: wide-band — fewer peaks per zero crossing"],
        ["Rainflow cycle counting", "Converts irregular time history into cycle amplitude histogram\nCounts full cycles by matching peaks/troughs (hysteresis loops)\nProvides nᵢ at each stress amplitude Sᵢ for Miner's rule\nRequired for accurate fatigue calculation from time-domain data"],
        ["Dirlik method", "Empirical closed-form fatigue damage from PSD directly:\nUses spectral moments m₀, m₁, m₂, m₄ of PSD\nPDF of peak amplitudes p(S) — four-parameter distribution\nMost accurate spectral method; preferred in industry (SAE, ASTM)"],
        ["Spectral moments", "mₙ = ∫ fⁿ·S(f) df  (nth spectral moment)\nm₀ = σ²;  m₂ = mean square velocity/ωₙ;  m₄ = mean square acceleration\nIrregularity factor α₁ = m₁/√(m₀m₂);  α₂ = m₂/√(m₀m₄)\nAll Dirlik parameters expressed in terms of spectral moments"],
        ["Miner's rule with PSD", "D = ν₀⁺·T·∫ p(S)·S^b dS / C  (Basquin: N·S^b = C)\nν₀⁺ = expected zero-crossing rate\nFor narrow-band + Rayleigh: D = ν₀⁺·T·(√2σ)^b·Γ(1+b/2)/C\nFailure: D = 1;  design: D_allowable ≈ 0.3–0.5"],
      ]} />

      <h2 style={S.h2}>Random Vibration Testing Standards</h2>
      <p style={S.p}>
        Random vibration testing subjects hardware to a specified PSD profile, reproducing the frequency content of the real-world environment in a compressed duration. Test specifications are defined by military, aerospace, and industrial standards and must be analytically related to the field environment via damage equivalence.
      </p>
      <DiagramTestingStandards />
      <Table2 rows={[
        ["Test profile construction", "Match area (g_rms) of measured field PSD over analysis bandwidth\nAdjust shape: accentuate frequencies where structure is sensitive\nTime compression: increase level to achieve same fatigue damage in less time\nFatigue equivalence: S₁^b T₁ = S₂^b T₂  (inverse power law)"],
        ["One-sided vs two-sided PSD", "Physical measurement: one-sided G(f) for f ≥ 0  [g²/Hz]\nMathematical: two-sided S(f) for all f;  G(f) = 2·S(f) for f > 0\nMiles' equation uses one-sided G (standard convention in testing)\nσ² = ∫₀^∞ G(f) df  (area under one-sided PSD)"],
        ["Level control tolerance", "Test standard: ±3 dB per frequency band (2× to 0.5× nominal level)\nControl accelerometer: measures average of collocated sensors\nLimiting: prevents over-test at specific resonances\nNotching: reduce level at structural resonance to limit response"],
        ["Test duration equivalence", "MIL-STD-810: test duration based on measured mission exposure\nField data → test PSD → equivalent duration via fatigue damage\nWorkmanship test (GEVS): 3 min/axis at 14.1 g_rms — finds loose connections\nLifetime simulation: may be 40–200 hours for automotive NVH"],
        ["Fixture requirements", "Rigid fixture: must transmit test PSD without resonances in test band\nFixture resonance < 2× control accel reading → must be managed\nCenter of mass height: affects rocking modes\nOver-test risk: fixture resonance amplifies input to structure"],
        ["Multi-axis testing", "Three axes applied simultaneously (where field environment correlated)\nCross-axis terms in PSD matrix: S_xy(f) off-diagonal\nSimultaneous multi-axis: more realistic but complex controller\nSequential single-axis: standard but may miss combined effects"],
      ]} />

      <h2 style={S.h2}>Non-Gaussian and Extreme Value Statistics</h2>
      <p style={S.p}>
        While the Gaussian assumption simplifies analysis enormously, many real vibration environments are non-Gaussian — particularly those involving impacts, impulsive loads, or nonlinear systems. Extreme value theory addresses the critical question: what is the largest response expected over the design lifetime?
      </p>
      <DiagramExtremeValue />
      <Table2 rows={[
        ["Non-Gaussian processes", "Kurtosis γ₂ > 3 (leptokurtic): heavier tails than Gaussian\nSources: impact loading, road/runway bumps, rocket ignition transients\nEffect: more frequent extreme excursions → accelerated fatigue\nTest requirement: kurtosis control in test PSD replication (ASTM E2579)"],
        ["Kurtosis control in testing", "Standard shaker test: Gaussian drive signal (γ₂ = 3 by construction)\nNon-Gaussian environments under-tested by Gaussian test\nKurtosion technique: adds controlled peaks to drive signal to achieve γ₂ = 4–7\nIncreases fatigue damage rate without changing PSD or g_rms"],
        ["Peak factor Davenport", "k_p = √(2 ln(ν_peak · T)) + 0.5772/√(2 ln(ν_peak · T))\nν_peak = fₙ for narrow-band process\nFor fₙ=10 Hz, T=3600 s: k_p ≈ 4.0\nx_max_expected = k_p · σ_X"],
        ["Extreme value distribution", "P(X_max ≤ u) = exp(−exp(−(u−μ)/β))  [Gumbel type I]\nμ = mode (most probable maximum)\nβ = scale parameter (related to bandwidth of distribution)\nP(X_max > u) = 1 − P(X_max ≤ u) — exceedance probability"],
        ["Return period", "T_return = 1/(f_exceedance)  [seconds] or [years]\nf_exceedance = P(X > u) × ν_peaks  [events per unit time]\n100-year return level: u such that P(exceed in 1 year) = 0.01\nUsed for wind, seismic, wave loading design"],
        ["Poisson upcrossing model", "ν(u) = ν₀ exp(−u²/(2σ²))  (for Gaussian, narrow-band)\nν₀ = expected zero-crossing rate (≈ fₙ)\nExpected number of crossings above u in time T: E[N_u] = ν(u)·T\nP(max > u in T) ≈ 1 − exp(−ν(u)·T)  (Poisson approximation)"],
      ]} />

      <InfoBox title="Key Formulae Summary" items={[
        "Wiener-Khinchin: S(f) = FT{R(τ)};  σ² = R(0) = ∫S(f)df",
        "SDOF random response: S_XX(f) = |H(f)|²·S_FF(f);  σ²_X = ∫|H(f)|²·S(f)df",
        "Miles equation: x_rms = √(π·fₙ·S₀/(2ζ));  a_rms [g] = √(π·fₙ·G_in/(2ζ))",
        "SRSS: σ_total = √(Σσᵣ²)  (well-separated modes);  CQC uses ρᵣₛ for close modes",
        "Rayleigh peak PDF: p(a) = (a/σ²)exp(−a²/2σ²);  P(a>nσ) = exp(−n²/2)",
        "Miner's rule: D = Σ(nᵢ/Nᵢ);  N·S^b = C (Basquin);  failure at D = 1",
        "Peak factor: k_p = √(2 ln(fₙ·T));  x_max = k_p·σ  (design limit)",
        "Gumbel extreme value: P(X_max ≤ u) = exp(−exp(−(u−μ)/β))",
      ]} />
    </div>
  );
}